<template>
  <UiSection id="prizepool" class="light">
    <div class="max-w-screen-lg mx-auto space-y-10">
      <h2 class="text-5xl font-extrabold uppercase" v-text="t('header')" />

      <div class="space-y-8 text-green-600">
        <div class="space-y-4 leading-4">
          <div class="flex justify-between items-center text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
            <b>
              {{ toStringAmount(total) }}
              <i class="fa fa-ruble-sign" />
            </b>
            <div class="text-green-400 hidden sm:block text-2xl sm:text-3xl md:text-4xl lg:text-5xl">
              <i class="fa fa-chevron-right" />
              <i class="fa fa-chevron-right" />
              <i class="fa fa-chevron-right" />
              <i class="fa fa-chevron-right" />
              <i class="fa fa-chevron-right" />
              <i class="fa fa-chevron-right" />
            </div>
            <b>
              {{ toStringAmount(goal) }}
              <i class="fa fa-ruble-sign" />
            </b>
          </div>

          <div class="rounded overflow-hidden text-sm bg-gray-500 text-right text-white text-opacity-75" :title="`Duel: 60% (~${duelTotalPrize} ${t('rub')})\nDraft: 40% (~${draftTotalPrize} ${t('rub')})`">
            <div class="relative flex overflow-hidden z-10 divide-x divide-gray-500 divide-opacity-25" :style="{ width: `${(100 * total) / goal}%` }">
              <div class="flex bg-gradient-to-br from-green-400 to-green-600 divide-x divide-green-700 divide-opacity-25" style="width: 60%">
                <small class="p-1" v-for="(prize, index) in duelPrizes" :key="index" :style="{ width: `${prize.percent}%` }" :title="`Duel\n${duelPrizes.length - index} ${t('place')}\n${prize.percent}% (~${toStringAmount(prize.rub)} ${t('rub')})`">
                  {{ duelPrizes.length - index }}
                </small>
              </div>
              <div class="flex rounded-r bg-gradient-to-br from-blue-400 to-blue-600 divide-x divide-blue-700 divide-opacity-25" style="width: 40%">
                <small class="p-1" v-for="(prize, index) in draftPrizes" :key="index" :style="{ width: `${prize.percent}%` }" :title="`Draft\n${draftPrizes.length - index} ${t('place')}\n${prize.percent}% (~${toStringAmount(prize.rub)} ${t('rub')})`">
                  {{ draftPrizes.length - index }}
                </small>
              </div>
            </div>
          </div>

          <div class="flex justify-between items-center opacity-50 text-xl md:text-2xl lg:text-3xl">
            <b>
              {{ t('current') }}: {{ toStringAmount(total / dollar) }}
              <i class="fa fa-dollar-sign" />
            </b>
            <b>
              {{ t('goal') }}: {{ toStringAmount(goal / dollar) }}
              <i class="fa fa-dollar-sign" />
            </b>
          </div>
        </div>
      </div>

      <div class="space-y-4">
        <h3 class="text-3xl font-extrabold" v-text="t('sponsors')" />
        <ul class="text-lg lg:text-xl space-y-4 font-bold">
          <li class="flex flex-col p-3 rounded bg-gray-500 bg-opacity-25 sm:flex-row sm:items-center text-2xl lg:text-3xl" v-for="(sponsor, index) in sponsors" :key="index">
            <div class="flex flex-1 items-center">
              <img class="h-10 w-10 mr-3 rounded" :src="sponsor.logo" alt v-if="sponsor.logo" />
              <a class="flex-1 text-blue-700" :href="sponsor.link" target="_blank" v-if="sponsor.link">{{ sponsor.nick }}</a>
              <div class="flex-1" v-else>{{ sponsor.nick }}</div>
            </div>

            <div class="flex mt-4 sm:mt-0 justify-end" v-if="sponsor.donate || (sponsor.prizes && sponsor.prizes.length)">
              <div v-if="sponsor.prizes && sponsor.prizes.length">
                <i class="fa ml-2" v-for="(prize, index) in sponsor.prizes" :key="index" :class="`fa-${prize}`" />
                <span class="mx-3" v-if="sponsor.donate">+</span>
              </div>
              <div class="text-right" v-if="sponsor.donate">
                {{ toStringAmount(sponsor.donate) }}
                <i class="fa fa-ruble-sign" />
                <small class="inline-block opacity-50 text-right" style="width: 4em" v-if="sponsor.donate">
                  {{ toStringAmount(sponsor.donate / dollar) }}
                  <i class="fa fa-dollar-sign" />
                </small>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <div class="flex flex-col space-y-10 items-stretch md:items-start md:flex-row md:space-x-24 md:space-y-0">
        <div class="flex-1 space-y-4">
          <h3 class="text-3xl font-extrabold" v-text="t('donators')" />
          <ul class="text-lg lg:text-xl divide-y divide-gray-500 divide-opacity-25 font-bold">
            <li class="flex items-center py-1" v-for="(donator, index) in donators" :key="index">
              <i class="rounded-sm flag-icon shadow-xs flex-shrink-0 mr-2" :class="`flag-icon-${donator.country}`" />
              <span class="flex-1">{{ donator.nick }}</span>
              <span class="text-right flex-shrink-0">
                {{ toStringAmount(donator.donate) }}
                <i class="fa fa-ruble-sign" />
              </span>
              <small class="opacity-50 text-right flex-shrink-0" style="width: 3.5em">
                {{ toStringAmount(donator.donate / dollar) }}
                <i class="fa fa-dollar-sign" />
              </small>
            </li>
          </ul>
        </div>

        <div class="space-y-4 whitespace-normal">
          <h3 class="text-3xl font-extrabold" v-text="t('donate')" />
          <p class="space-x-6 text-4xl">
            <a class="inline-block space-x-2 transform scale-125 mx-3" href="https://money.yandex.ru/to/410014326465674?comment=Donation%20for%20tournament" target="_blank">
              <i class="fab fa-cc-visa" />
              <i class="fab fa-cc-mastercard" />
            </a>
            <a href="https://money.yandex.ru/to/410014326465674?comment=Donation%20for%20tournament" target="_blank">
              <i class="fab fa-yandex" />
            </a>
            <a href="https://www.paypal.me/deathmatchtv" target="_blank">
              <i class="fab fa-paypal" />
            </a>
            <a href="https://qiwi.com/n/DEATHMATCHTV" target="_blank">
              <i class="fas fa-kiwi-bird" />
            </a>
          </p>
          <p v-html="t('issues')" />
          <p v-html="t('inquiries')" />
          <p v-html="t('thanks')" />
        </div>
      </div>

      <div class="space-y-2 text-gray-600 text-base whitespace-normal">
        <b v-text="t('disclaimerHeader')" />
        <p v-html="t('disclaimerText')" />
      </div>
    </div>
  </UiSection>
</template>

<script>
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { dollar, goal, menu, participants } from '../../store.js'

  export default {
    name: 'SectionPrizepool',
    setup() {
      const active = ref('all')

      const donators = computed(() => {
        const list = participants.filter(participant => participant.donate && !participant.logo)
        list.sort((p1, p2) => (p1.donate > p2.donate ? -1 : 1))
        return list
      })

      const sponsors = computed(() => {
        const list = participants.filter(participant => participant.logo)
        list.sort((p1, p2) => (p1.donate > p2.donate ? -1 : 1))
        return list
      })

      const total = computed(() => {
        const fromDonators = donators.value.map(donator => donator.donate || 0).reduce((a, b) => a + b, 0)
        const fromSponsors = sponsors.value.map(sponsor => sponsor.donate || 0).reduce((a, b) => a + b, 0)
        return fromDonators + fromSponsors
      })

      const duelTotalPrize = computed(() => {
        return Math.floor((60 * total.value) / 100000) * 1000
      })
      const draftTotalPrize = computed(() => {
        return Math.floor((40 * total.value) / 100000) * 1000
      })

      const duelPrizes = computed(() => {
        const all = (60 * total.value) / 100
        return [
          { percent: 30, rub: Math.floor((30 * all) / 100000) * 1000 },
          { percent: 20, rub: Math.floor((20 * all) / 100000) * 1000 },
          { percent: 15, rub: Math.floor((15 * all) / 100000) * 1000 },
          { percent: 12, rub: Math.floor((12 * all) / 100000) * 1000 },
          { percent: 8, rub: Math.floor((8 * all) / 100000) * 1000 },
          { percent: 8, rub: Math.floor((8 * all) / 100000) * 1000 },
          { percent: 3.5, rub: Math.floor((3.5 * all) / 100000) * 1000 },
          { percent: 3.5, rub: Math.floor((3.5 * all) / 100000) * 1000 },
        ].reverse()
      })

      const draftPrizes = computed(() => {
        const all = (40 * total.value) / 100
        return [
          { percent: 40, rub: Math.floor((40 * all) / 100000) * 1000 },
          { percent: 30, rub: Math.floor((30 * all) / 100000) * 1000 },
          { percent: 20, rub: Math.floor((20 * all) / 100000) * 1000 },
          { percent: 10, rub: Math.floor((10 * all) / 100000) * 1000 },
        ].reverse()
      })

      function toStringAmount(donate = 0) {
        const string = Math.round(donate).toString()
        return donate / 10000 > 1 ? string.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : string
      }

      const { t } = useI18n({
        messages: {
          en: {
            header: 'Prize pool',
            // total: 'Total',
            // duels: 'Duels',
            // draft: 'Draft',
            current: 'Current',
            goal: 'Goal',
            rub: 'rub',
            place: 'place',
            sponsors: 'Sponsors',
            donators: 'Donators',
            donate: 'Donate',
            issues: `
              If you have any issues,<br />
              contact DeathmatchTV#1405 on <a href="https://discord.gg/TFMJevK" target="_blank">Discord</a>.
            `,
            inquiries: `
              Business inquiries:<br />
              deathmatchtv&commat;outlook.com
            `,
            thanks: `
              <b>Thanks a lot for your support!</b>
            `,
            disclaimerHeader: 'Disclaimer',
            disclaimerText: `
              The value of prizes and awards/gifts to individuals is considered taxable income.
              Merchandise or products won as a prize or award will be considered at the fair market value and could also be considered taxable income.
              Nonresident aliens may be subject to additional tax rules regardless of amount, depending on circumstances and treaty status.
            `,
            distribution: 'The distribution details will be revealed later...',
          },
          ru: {
            header: 'Призовой фонд',
            // total: 'Всего',
            // duels: 'Дуэли',
            // draft: 'Драфт',
            current: 'Текущий',
            goal: 'Цель',
            rub: 'Руб',
            place: 'место',
            sponsors: 'Спосноры',
            donators: 'Донаторы',
            donate: 'Поддержка',
            issues: `
              Если возникли какие-то проблемы,<br />
              свяжитесь с DeathmatchTV#1405 в <a href="https://discord.gg/TFMJevK" target="_blank">Discord</a>.
            `,
            inquiries: `
              Деловые предложения:<br />
              deathmatchtv&commat;outlook.com
            `,
            thanks: `
              <b>Большое спасибо за поддержку!</b>
            `,
            disclaimerHeader: 'Предупреждение',
            disclaimerText: `
              Денеждные награды/подарки физическим лицам считаются налогооблагаемым доходом.
              Товары или продукты, выигранные в качестве приза, будут рассчитываться по рыночной стоимости, и также могут считаться налогооблагаемым доходом.
              Для иностранцев-нерезидентов могут быть дополнительным налоговые правила в зависимости от обстоятельств.
            `,
            distribution: 'Про распределение призов будет рассказано позже...',
          },
        },
      })

      menu.prizepool = {
        name: computed(() => t('header')),
        icon: 'fa fa-money-bill-wave',
      }

      return { active, dollar, goal, donators, sponsors, total, duelPrizes, draftPrizes, duelTotalPrize, draftTotalPrize, toStringAmount, t }
    },
  }
</script>

<style scoped>
  .progress > div > div {
    width: 200%;
    margin-left: -100%;
    background-image: linear-gradient(to right, var(--gradient-from-color) 0%, var(--gradient-to-color) 25%, var(--gradient-from-color) 50%, var(--gradient-to-color) 75%, var(--gradient-from-color) 100%);
    animation: translating 1s linear infinite;
  }
  .donator {
    color: #300;
  }
  .donator:nth-child(1) {
    color: #f00;
  }
  .donator:nth-child(2) {
    color: #e00;
  }
  .donator:nth-child(3) {
    color: #d00;
  }
  .donator:nth-child(4) {
    color: #c00;
  }
  .donator:nth-child(5) {
    color: #b00;
  }
  .donator:nth-child(6) {
    color: #a00;
  }
  .donator:nth-child(7) {
    color: #900;
  }
  .donator:nth-child(8) {
    color: #800;
  }
  .donator:nth-child(9) {
    color: #700;
  }
  .donator:nth-child(10) {
    color: #600;
  }
  .donator:nth-child(11) {
    color: #500;
  }
  .donator:nth-child(12) {
    color: #400;
  }
  @keyframes translating {
    0% {
      transform: translate(0%);
    }
    100% {
      transform: translate(50%);
    }
  }
</style>
